import React, { useCallback } from "react";
import { Stack, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import HeaderButton from "@/modules/lead-page/new/components/task-management/header-button";
import {
  Banknote,
  Building2,
  ClipboardList,
  Heart,
  Lightbulb,
  Newspaper,
  Star,
  ListTodo,
  Milestone,
  type LucideProps,
} from "lucide-react";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import LastActivityCard from "@/modules/lead-page/new/components/activity/last-acticity-card";
import { ActivityProvider } from "@/modules/lead-page/new/context/activity.context";
import LastActivityDate from "../activity/last-activity-date";

interface MobileActionCardsProps {
  isLeadArchived: boolean;
  onTasksClick: () => void;
  onNextActionsClick: () => void;
  onRecommendationsClick: () => void;
  onActivitiesClick: () => void;
  onSalesClick: () => void;
  onPropertiesClick: () => void;
  onInterestsClick: () => void;
  onDeveloperTracksClick: () => void;
  onRatingsClick: () => void;
  isRatingsOpen: boolean;
  isDeveloperTracksVisible: boolean;
}

export interface ActionCard {
  title: string;
  onClick: () => void;
  icon: React.ComponentType<LucideProps>;
  show?: boolean; // Optional condition to show/hide the card
  body?: React.ReactNode;
  caption?: React.ReactNode;
}

const MobileActionCards: React.FC<MobileActionCardsProps> = ({
  isLeadArchived,
  onTasksClick,
  onNextActionsClick,
  onRecommendationsClick,
  onActivitiesClick,
  onSalesClick,
  onPropertiesClick,
  onInterestsClick,
  onDeveloperTracksClick,
  onRatingsClick,
  isRatingsOpen,
  isDeveloperTracksVisible,
}) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleNavigateToTrack = useCallback((trackUId: string) => {
    searchParams.set("trackUId", trackUId);
    setSearchParams(searchParams, { replace: true });
    onDeveloperTracksClick();
  }, [searchParams, setSearchParams, onDeveloperTracksClick]);

  const actionCards: ActionCard[] = [
    {
      title: "Activity feed",
      caption: (
        <ActivityProvider>
          <LastActivityDate />
        </ActivityProvider>
      ),
      body: (
        <ActivityProvider>
          <LastActivityCard onNavigateToTrack={handleNavigateToTrack} />
        </ActivityProvider>
      ),
      onClick: onActivitiesClick,
      icon: Newspaper,
    },
    {
      title: "Tasks",
      caption: "Manage lead tasks.",
      onClick: onTasksClick,
      icon: ListTodo,
    },
    {
      title: "Next actions",
      caption: "Tasks & reminders.",
      onClick: onNextActionsClick,
      icon: ClipboardList,
    },
    {
      title: "Recommendations",
      caption: "Similar properties.",
      onClick: onRecommendationsClick,
      icon: Lightbulb,
    },

    {
      title: "Sales",
      caption: "Sales information.",
      onClick: onSalesClick,
      icon: Building2,
    },
    {
      title: "Properties",
      caption: "Property details.",
      onClick: onPropertiesClick,
      icon: Banknote,
    },
    {
      title: "Interests",
      caption: "Interests details.",
      onClick: onInterestsClick,
      icon: Heart,
    },
    ...(isDeveloperTracksVisible
      ? [
          {
            title: "Developer Tracks",
            caption: "Track progress details.",
            onClick: onDeveloperTracksClick,
            icon: Milestone,
          },
        ]
      : []),
    ...(isRatingsOpen
      ? [
          {
            title: "Reviews",
            caption: "Reviews details.",
            onClick: onRatingsClick,
            icon: Star,
          },
        ]
      : []),
  ];

  return (
    <Stack direction="column" gap={theme.nawy.spacing.sm}>
      {actionCards
        .filter((card) => card.show !== false) // Filter out cards where show is explicitly false
        .map((card) => {
          const IconComponent = card.icon;
          return (
            <ArchivedOverlay key={card.title} isArchived={isLeadArchived}>
              <HeaderButton
                title={card.title}
                caption={card.caption}
                onClick={card.onClick}
                body={card.body}
                icon={
                  <IconComponent
                    color={theme.nawy.colors.semantic.text.accent}
                  />
                }
              />
            </ArchivedOverlay>
          );
        })}
    </Stack>
  );
};

export default MobileActionCards;
